<script context="module" lang="ts">
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import DirectusLink from '$lib/components/navigation/DirectusLink.svelte';
	import Slider from '$lib/components/util/Slider.svelte';
	import DirectusMediaSlider from '$lib/components/util/DirectusMediaSlider.svelte';
	import { gql } from 'graphql-request';
	import Media from '$lib/components/media/Media.svelte';
	import { getText } from '$lib/util/translations';
	import { page } from '$app/stores';

	import { PUBLIC_DIRECTUS_BACKEND } from '$env/static/public';

	import Section from '$lib/components/layout/Section.svelte';
	import Text from '$lib/components/Text.svelte';
	import Loader from '$lib/components/util/Loader.svelte';
	import Input from '$lib/components/inputs/Input.svelte';
	import Textarea from '$lib/components/inputs/Textarea.svelte';
	import Button from '$lib/components/util/Button.svelte';

	export const config: SectionConfig = {
		collection: 'section_form',
		availableFor: ['pages', 'special-pages'],
		query: gql`
      translations {
        languages_code {
          code
        }
        title
        subtitle
				text
      }
			background {
				...Media
			}
      form {
        name
        privacy_link {
          ...Link
        }
        fields {
          name
          type
          width
          required
          translations {
            languages_code {
              code
            }
            label
          }
        }
				translations {
					languages_code {
						code
					}
					success_message
				}
      }
    `
	};

	// optional: define a type for the props (note: injectTranslations is applied)
	type Props = {
		title: string;
		subtitle: string;
		form: any;
	};
</script>

<script lang="ts">
	import TextWithIcon from '$lib/components/util/TextWithIcon.svelte';
	import { enhance } from '$app/forms';

	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)

	let loading: boolean = false;
	let globalError: string = '';
	let success: boolean = false;
	let errors: any = {};
	let formData: any = {};

	let fields = data.form.fields;

	for (const field of data.form.fields) {
		formData[field.name] = '';
	}

	async function submitContactform(event: Event) {
		event.preventDefault();

		/*
		Depending on the version of the directus extension endpoint, directus
		will prefix directus-extension- to the directory name (somewhere around version 10.2)
		*/
		const url = PUBLIC_DIRECTUS_BACKEND + 'directus-extension-custom-form/';
		// await recaptchaLoaded()
		// const token = await executeRecaptcha('login')
		errors = {};
		globalError = '';
		loading = true;
		success = false;

		// Set form data
		const finalFormData = {
			form: data.form.name,
			...formData,
			token: null
		};

		// Send form
		const res = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(finalFormData)
		});
		const resData = await res.json();

		// Interpret server response
		if (resData.success) {
			resetForm();
			success = true;
		} else {
			for (const e of resData.errors) {
				if (e.field !== '') {
					errors[e.field] = getText($page.data.globalTexts, e.error).text;
				} else {
					globalError = getText($page.data.globalTexts, e.error).text;
				}
			}
		}

		loading = false;
	}

	function resetForm() {
		errors = {};
		globalError = '';
		success = false;

		for (const field of data.form.fields) {
			formData[field] = '';
		}
	}
</script>

<Section gapType="padding">
	<div class="background" slot="background">
		<div class="background-image-wrapper">
			<Media {...data.background} fit="cover" transform="xl" />
		</div>
	</div>

	<div class="card">
		<div class="left">
			<p class="subtitle">{data.subtitle ?? ''}</p>
			<h2>{data.title ?? ''}</h2>
			<p class="text">{data.text ?? ''}</p>
		</div>
		<div class="right">
			<form class="contact-form">
				<div class="row">
					<div class="row-group">
						<div class="row-text">
							<Text key="form_text_1" />
						</div>

						<div class="input-wrap" class:error={fields[0].name in errors}>
							<Input
								id={`input-${data.form.name}-${fields[0].name}`}
								bind:value={formData[fields[0].name]}
								placeholder={fields[0].label}
								type="text"
								name={fields[0].name}
							/>
						</div>
					</div>
					<div class="row-group">
						<div class="row-text">
							<Text key="form_text_2" />
						</div>
						<div class="input-wrap" class:error={fields[1].name in errors}>
							<Input
								id={`input-${data.form.name}-${fields[1].name}`}
								bind:value={formData[fields[1].name]}
								placeholder={fields[1].label}
								type="text"
								name={fields[1].name}
							/>
						</div>
					</div>
				</div>

				<div>
					<div class="title">
						<Text key="form_text_3" />
					</div>
					<div class="areas">
						<button
							type="button"
							class="area"
							class:active={formData['business_area'] === 'Horeca'}
							on:click={() => {
								formData['business_area'] = 'Horeca';
							}}
						>
							<Text key="form_horeca" />
						</button>
						<button
							type="button"
							class="area"
							class:active={formData['business_area'] === 'Distributor'}
							on:click={() => {
								formData['business_area'] = 'Distributor';
							}}
						>
							<Text key="form_distributor" />
						</button>
						<button
							type="button"
							class="area"
							class:active={formData['business_area'] === 'Retail'}
							on:click={() => {
								formData['business_area'] = 'Retail';
							}}
						>
							<Text key="form_retail" />
						</button>
						<button
							type="button"
							class="area"
							class:active={formData['business_area'] === 'Franchise'}
							on:click={() => {
								formData['business_area'] = 'Franchise';
							}}
						>
							<Text key="form_franchise" />
						</button>
						<button
							type="button"
							class="area"
							class:active={formData['business_area'] === 'Office'}
							on:click={() => {
								formData['business_area'] = 'Office';
							}}
						>
							<Text key="form_office" />
						</button>
						<button
							type="button"
							class="area"
							class:active={formData['business_area'] === 'Events'}
							on:click={() => {
								formData['business_area'] = 'Events';
							}}
						>
							<Text key="form_events" />
						</button>
						<button
							type="button"
							class="area"
							class:active={formData['business_area'] === 'Other'}
							on:click={() => {
								formData['business_area'] = 'Other';
							}}
						>
							<Text key="form_other" />
						</button>
					</div>
				</div>

				<div>
					<div class="title">
						<Text key="form_text_4" />
					</div>
					<div class:error={fields[3].name in errors}>
						<Textarea
							id={`input-${data.form.name}-${fields[3].name}`}
							bind:value={formData[fields[3].name]}
							placeholder={fields[3].label}
							label=""
							name={fields[3].name}
						/>
					</div>
				</div>

				<div class="row">
					<div class="row-text">
						<Text key="form_text_5" />
					</div>
					<div class="input-wrap" class:error={fields[4].name in errors}>
						<Input
							id={`input-${data.form.name}-${fields[4].name}`}
							bind:value={formData[fields[4].name]}
							placeholder={fields[4].label}
							type="text"
							name={fields[4].name}
						/>
					</div>
				</div>

				<div class="row">
					<div class="row-text">
						<Text key="form_text_6" />
					</div>
					<div class="input-wrap" class:error={fields[5].name in errors}>
						<Input
							id={`input-${data.form.name}-${fields[5].name}`}
							bind:value={formData[fields[5].name]}
							placeholder={fields[5].label}
							type="text"
							name={fields[5].name}
						/>
					</div>
				</div>

				<Button inverted on:click={submitContactform} textKey="submit" iconKey="icon-arrow-right">
					<span slot="custom_before" class="loader-wrapper">
						{#if loading}
							<span style="margin-inline-start: 8px">
								<Loader size={16} width={3} color="#fed671" />
							</span>
						{/if}
					</span>
				</Button>
				{#if globalError || success || (errors && Object.values(errors).length > 0)}
					<div class="messages">
						{#if globalError}
							<div class="form-error">{globalError}</div>
						{/if}
						{#if errors && Object.values(errors).length > 0}
							{#each Object.entries(errors) as [key, value]}
								<div class="form-error"><Text key={'form_' + key} tag="span" />: {value}</div>
							{/each}
						{/if}
						{#if success}
							<div class="success-msg">{data.form?.success_message}</div>
						{/if}
					</div>
				{/if}
			</form>
		</div>
	</div>
</Section>

<style lang="scss">
	h2 {
		@include font(h2);
		margin-bottom: v(content-padding);
		hyphens: none;

		@include media(phone) {
			hyphens: auto;
		}
	}

	.background,
	.background-image-wrapper {
		width: 100%;
		height: 100%;
	}

	.subtitle {
		@include font(h6);
		margin-bottom: v(content-padding);
	}

	.text {
		@include font(text);
	}

	.card {
		display: grid;
		gap: v(content-padding-xl);
		grid-template-columns: 1fr 1fr;

		@media (max-width: 1600px) {
			grid-template-columns: 2fr 3fr;
		}

		@media (max-width: 900px) {
			grid-template-columns: 1fr;
		}

		.right {
			background: color(white);
			border-radius: 4px;
			padding: v(content-padding-lg);
		}
	}

	form {
		display: flex;
		flex-direction: column;
		gap: v(content-padding-xl);

		@include font(h6);

		.row {
			width: 100%;
			display: flex;
			align-items: center;
			gap: v(content-padding-sm);

			@media (max-width: 1400px) {
				flex-wrap: wrap;
			}
		}

		.row-group {
			display: flex;
			gap: v(content-padding-sm);
			flex-wrap: nowrap;
			align-items: center;
			width: 100%;
		}

		.title {
			margin-bottom: v(content-padding-xs);
		}

		.input-wrap {
			width: 100%;
		}

		.row-text {
			flex-shrink: 0;
		}

		:global(input) {
			margin-bottom: 0;
			padding: 4px 8px;
			width: 100%;
		}

		:global(textarea) {
			border: 0;
			border-bottom: 1px solid color(grey_03);
			background: color(grey_01);
			min-height: 100px;
			@include font(text);
		}

		:global(button) {
			display: inline-flex;
			justify-content: center !important;
		}
	}

	.areas {
		:global(button) {
			border: 0;
			background: color(grey_02);
			@include font(h6);
			padding: 8px 16px;
			margin-bottom: 4px;
			border-radius: 4px;

			&:hover {
				background: color(grey_03);
			}

			&.active {
				background: color(yellow);
			}
		}
	}

	input,
	textarea {
		margin: 8px 0;
	}

	.privacy-hint {
		display: flex;
		align-items: center;
		@include font(text);
	}

	.messages {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.form-error,
	.success-msg {
		@include font(text);
	}

	.loader-wrapper {
		height: 16px;
		margin: 0 !important;

		:global(.loader-wrapper) {
			margin-top: 0;
		}
	}
</style>
